import Decimal from 'decimal.js';

/**
 * Lãi lỗ công trình (anh Tiến 28/10/2026, DEC-571; docs/phases/P21/lai-lo-cong-trinh.md).
 * Doanh thu = giá trị đã làm theo báo cáo ngày (đơn giá HĐ CĐT, chưa VAT; DEC-540). Chi phí = khoán tổ đội đã nghiệm thu (tự lấy từ Đội khoán)
 * + chi phí khác kế toán ghi theo nhóm (vật tư theo phiếu xuất Asia, nhân công công ty, máy, vận chuyển…). Có dự toán theo nhóm để tính lãi dự kiến.
 */
export const SITE_COST_CATEGORIES = ['material', 'company_labor', 'equipment', 'transport', 'site_overhead', 'other'] as const;
export type SiteCostCategory = (typeof SITE_COST_CATEGORIES)[number];
export const SITE_COST_CATEGORY_LABELS: Record<SiteCostCategory, string> = {
  material: 'Vật tư',
  company_labor: 'Nhân công công ty',
  equipment: 'Máy, thiết bị thuê',
  transport: 'Vận chuyển, cẩu',
  site_overhead: 'Chi phí công trường (lán trại, điện nước, ăn ở)',
  other: 'Khác',
};

export interface SiteProfitInput {
  /** Giá trị HĐ CĐT chưa VAT (HĐ đã nhập, chưa có thì tổng hạng mục) */
  contractValue: number;
  /** Giá trị đã làm theo báo cáo ngày, chưa VAT */
  doneValue: number;
  /** Khoán tổ đội: tổng giá trị HĐ khoán còn hiệu lực (HĐ xong lấy số đã nghiệm thu) */
  crewCommitted: number;
  /** Khoán tổ đội đã nghiệm thu (đợt đã duyệt / đã chi) */
  crewAccepted: number;
  /** Chi phí khác theo nhóm: dự toán, thực tế */
  costs: Partial<Record<SiteCostCategory, { budget: number; actual: number }>>;
}

const pct = (a: number, b: number) => (b > 0 ? new Decimal(a).div(b).mul(100).toDecimalPlaces(1, Decimal.ROUND_HALF_UP).toNumber() : null);

/** Lãi tạm tính (đến nay) và lãi dự kiến (cả công trình). Dự kiến lấy số lớn hơn giữa dự toán và thực tế từng nhóm. */
export function siteProfit(i: SiteProfitInput) {
  const cats = Object.values(i.costs);
  const otherActual = cats.reduce((a, c) => a + (c?.actual ?? 0), 0);
  const otherForecast = cats.reduce((a, c) => a + Math.max(c?.budget ?? 0, c?.actual ?? 0), 0);
  const costToDate = i.crewAccepted + otherActual;
  const profitToDate = i.doneValue - costToDate;
  const costForecast = Math.max(i.crewCommitted, i.crewAccepted) + otherForecast;
  const profitForecast = i.contractValue - costForecast;
  return {
    otherActual, otherForecast, costToDate, profitToDate, marginToDate: pct(profitToDate, i.doneValue),
    costForecast, profitForecast, marginForecast: pct(profitForecast, i.contractValue),
  };
}
